<template>
  <v-card class="runner-card" outlined>
    <v-tabs :value="activeIndex" show-arrows color="primary" class="test-tabs" @change="$emit('change', $event)">
      <v-tab v-for="(test, testIndex) in tests" :key="test.key" class="test-tab">
        <v-icon left small :color="statusColor(test)">{{ statusIcon(test) }}</v-icon>
        {{ testIndex + 1 }}. {{ test.name }}
      </v-tab>
    </v-tabs>

    <v-divider />

    <v-card-text class="runner-body">
      <div class="test-meta">
        <div>
          <div class="overline primary--text">第 {{ activeIndex + 1 }} 项 / 共 {{ tests.length }} 项</div>
          <h2 class="text-h5 font-weight-bold mb-1">{{ currentTest.name }}</h2>
          <div class="subtitle-1 grey--text text--darken-1">{{ currentTest.description }}</div>
        </div>
        <v-chip :color="clearStatus.color" outlined><v-icon left small>{{ clearStatus.icon }}</v-icon>{{ clearStatus.label }}</v-chip>
      </div>

      <v-row class="mt-4" align="stretch">
        <v-col cols="12" md="7">
          <div class="section-label">本次发送内容</div>
          <pre class="payload">{{ payloadPreview(currentTest) }}</pre>
        </v-col>
        <v-col cols="12" md="5">
          <div class="section-label">设备响应</div>
          <div class="response-panel">
            <div v-if="!currentTest.responseList.length" class="response-empty">
              <v-icon large color="grey lighten-1">mdi-message-processing-outline</v-icon>
              <span>发送测试命令后等待设备响应</span>
            </div>
            <pre v-else>{{ formatJson(currentTest.responseList.length === 1 ? currentTest.responseList[0] : currentTest.responseList) }}</pre>
          </div>
        </v-col>
      </v-row>

      <v-alert v-if="currentTest.error" type="error" dense text class="mt-3 mb-0">{{ currentTest.error }}</v-alert>

      <div class="action-bar mt-5">
        <v-btn :disabled="activeIndex === 0" icon outlined title="上一项" @click="$emit('previous')"><v-icon>mdi-chevron-left</v-icon></v-btn>
        <v-btn :disabled="!connected || currentTest.sending" :loading="currentTest.sending" color="primary" large depressed class="send-button" @click="$emit('send')">
          <v-icon left>mdi-send</v-icon>发送测试命令
          <span class="shortcut">Enter</span>
        </v-btn>
        <v-btn :disabled="!hasSuccessfulResponse(currentTest)" :color="currentTest.confirmed === true ? 'success' : undefined" large outlined @click="$emit('finish', true)"><v-icon left>mdi-check</v-icon>成功并下一项</v-btn>
        <v-btn :disabled="!currentTest.sent" :color="currentTest.confirmed === false ? 'error' : undefined" large outlined @click="$emit('finish', false)"><v-icon left>mdi-close</v-icon>失败并下一项</v-btn>
        <v-btn :disabled="activeIndex === tests.length - 1" icon outlined title="下一项" @click="$emit('next')"><v-icon>mdi-chevron-right</v-icon></v-btn>
      </div>
      <div class="keyboard-hint text-center mt-3 grey--text"><v-icon small color="grey">mdi-keyboard-outline</v-icon> 回车发送，左右方向键切换项目；切换时自动关闭全部灯带</div>
    </v-card-text>
  </v-card>
</template>

<script>
export default {
  name: 'ProtocolTestRunner',
  props: {
    tests: { type: Array, required: true },
    activeIndex: { type: Number, required: true },
    connected: Boolean,
    clearState: { type: Object, required: true },
    payloadPreview: { type: Function, required: true },
    formatJson: { type: Function, required: true },
    hasSuccessfulResponse: { type: Function, required: true }
  },
  computed: {
    currentTest () { return this.tests[this.activeIndex] },
    clearStatus () {
      if (!this.connected) return { color: 'grey', icon: 'mdi-power-plug-off', label: '等待连接后清灯' }
      if (this.clearState.sending) return { color: 'accent', icon: 'mdi-loading mdi-spin', label: '正在关闭全部灯带' }
      if (this.clearState.error) return { color: 'error', icon: 'mdi-alert', label: '清灯失败' }
      if (this.clearState.skipped) return { color: 'primary', icon: 'mdi-lightbulb-on-outline', label: '保留上一项灯效' }
      if (this.clearState.success) return { color: 'success', icon: 'mdi-lightbulb-off-outline', label: '已关闭全部灯带' }
      return { color: 'primary', icon: 'mdi-lightbulb-off-outline', label: '已发送全关命令' }
    }
  },
  methods: {
    statusColor (test) { return test.confirmed === true ? 'success' : test.confirmed === false ? 'error' : test.sent ? 'accent' : 'grey' },
    statusIcon (test) { return test.confirmed === true ? 'mdi-check-circle' : test.confirmed === false ? 'mdi-close-circle' : test.sent ? 'mdi-send-circle' : 'mdi-circle-outline' }
  }
}
</script>

<style scoped>
.runner-card { border-color: #dfe3e8 !important; overflow: hidden; }
.test-tabs { background: #fafbfc; }
.test-tab { letter-spacing: 0; text-transform: none; min-width: 150px; }
.runner-body { padding: 28px; }
.test-meta { display: flex; align-items: flex-start; justify-content: space-between; gap: 20px; }
.section-label { color: #607d8b; font-size: .78rem; font-weight: 700; margin-bottom: 7px; }
.payload, .response-panel { background: #f6f8fa; border: 1px solid #dfe3e8; border-radius: 4px; min-height: 220px; max-height: 320px; overflow: auto; padding: 16px; font-size: .78rem; white-space: pre-wrap; word-break: break-word; margin: 0; }
.response-panel pre { margin: 0; white-space: pre-wrap; word-break: break-word; }
.response-empty { min-height: 186px; display: flex; flex-direction: column; align-items: center; justify-content: center; gap: 8px; color: #90a4ae; text-align: center; }
.action-bar { display: flex; align-items: center; justify-content: center; gap: 10px; flex-wrap: wrap; }
.send-button { min-width: 210px !important; }
.shortcut { margin-left: 12px; padding: 2px 6px; border: 1px solid rgba(255,255,255,.55); border-radius: 3px; font-size: .68rem; }
.keyboard-hint { font-size: .78rem; }
@media (max-width: 700px) { .runner-body { padding: 18px; } .test-meta { flex-direction: column; } .action-bar .v-btn { flex-grow: 1; } .action-bar .v-btn--icon { flex-grow: 0; } }
</style>
